import React, {useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import assistant from 'helpers/assistant';
import StyledTableBar from 'components/organisms/Bars/TableBar/TableBar.styles';
import constants from 'helpers/constants';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Search from '@mui/icons-material/Search';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import ViewColumnSharp from '@mui/icons-material/ViewColumnSharp';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import FilterList from '@mui/icons-material/FilterList';
import Badge from 'components/atoms/Badges/Badge/Badge';
import Tooltip from 'components/atoms/Tooltips/Tooltip/Tooltip';
import {Span} from 'components/atoms/Text/Typography/Typography';
import Save from '@mui/icons-material/Save';
import useMediaQuery from '@mui/material/useMediaQuery';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import Box from 'components/atoms/Layout/Box/Box';
import Close from '@mui/icons-material/Close';
import Toc from '@mui/icons-material/Toc';
import ViewHeadline from '@mui/icons-material/ViewHeadline';

const TableBar = React.forwardRef((props, ref) => {
  const {
    searchField,
    assistantAction,
    indexAction,
    viewAction,
    filterAction,
    columnAction,
    saveAction,
    hideSearch,
    hideIndex,
    hideAssistant,
    hideView,
    hideFilter,
    hideColumn,
    hideSave,
    ...innerProps
  } = useComponentProps({
    hideSave: true,
    hideIndex: true,
    ...props
  }, 'TableBar');

  const searchFieldRef = useRef(null);
  const tableProvider = useTable();
  const isLoading = tableProvider.isLoading();

  const mdDown = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const filterCount = utils.filterCount(tableProvider.listState.filter);
  const assistantCount = assistant.processedCount(tableProvider.assistantState?.assistantType, tableProvider.assistantState?.assistantConversation);

  const setSearchEvent = useEffectEvent(tableProvider.listState.setSearch);
  const handleSearch = (field, value) => {
    setSearchEvent?.(value);
  }

  const searchFields = useMemo(() => {
    const initialSearch = tableProvider.listState?.search?.replace(/(\n)+/g, ' ');

    return [{
      name: 'search',
      label: !mdDown ? 'Search' : null,
      type: constants.formFieldTypes.text,
      validation: constants.formFieldValidationTypes.text,
      initial: initialSearch,
      debounce: constants.debounce.search,
      FormFieldProps: {
        ref: searchFieldRef,
        autoFocus: false,
        hiddenLabel: true,
        size: 'smaller',
        radius: 'round'
      },
      prefix: <Icon icon={Search} />,
      postfix: <Box className="TableBar-search-adornment">
        {(initialSearch?.length > 0) ? <ActionIconButton action={{
          icon: <Icon icon={Close}/>,
          onClick: (e) => {
            setSearchEvent('');
            searchFieldRef.current?.focus();
            e.preventDefault();
          }
        }}/> : null}
      </Box>,
      ...searchField
    }];
  }, [searchField, mdDown, tableProvider.listState?.search, setSearchEvent]);

  const toggleSaveEvent = useEffectEvent(tableProvider.toggleSave);
  const saveActionMemo = useMemo(() => ({
    onClick: () => {
      toggleSaveEvent?.();
    },
    label: !mdDown ? 'Save' : null,
    tooltip: mdDown ? 'Save' : null,
    icon: Save,
    ...saveAction,
    ActionButtonProps: {
      color: 'primary',
      variant: 'contained',
      radius: 'round',
      ...saveAction?.ActionButtonProps
    },
    ActionIconButtonProps: {
      color: 'primary',
      variant: 'contained',
      ...saveAction?.ActionIconButtonProps
    }
  }), [saveAction, mdDown, toggleSaveEvent]);

  const toggleColumnsEvent = useEffectEvent(tableProvider.toggleColumns);
  const columnActionMemo = useMemo(() => ({
    onClick: () => {
      toggleColumnsEvent?.();
    },
    label: !mdDown ? 'Columns' : null,
    tooltip: mdDown ? 'Columns' : null,
    icon: ViewColumnSharp,
    ...columnAction,
    ActionButtonProps: {
      variant: 'outlined',
      plainBorder: true,
      color: 'inherit',
      size: 'medium',
      ...columnAction?.ActionButtonProps
    },
    ActionIconButtonProps: {
      ...columnAction?.ActionIconButtonProps
    }
  }), [columnAction, mdDown, toggleColumnsEvent]);

  const toggleIndexEvent = useEffectEvent(tableProvider.toggleIndex);
  const indexActionMemo = useMemo(() => ({
    onClick: () => {
      toggleIndexEvent?.();
    },
    label: !mdDown ? 'Index' : null,
    tooltip: mdDown ? 'Index' : null,
    icon: Toc,
    ...indexAction,
    ActionButtonProps: {
      color: 'default',
      variant: 'outlined',
      plainBorder: true,
      radius: 'round',
      ...indexAction?.ActionButtonProps
    },
    ActionIconButtonProps: {
      ...indexAction?.ActionIconButtonProps
    }
  }), [indexAction, mdDown, toggleIndexEvent]);

  const toggleAssistantEvent = useEffectEvent(tableProvider.toggleAssistant);
  const clearAssistantEvent = useEffectEvent(tableProvider.assistantState.clear);
  const assistantActionMemo = useMemo(() => ({
    onClick: () => {
      toggleAssistantEvent?.();
    },
    label: !mdDown ? 'Assistant' : null,
    tooltip: mdDown ? 'Assistant' : null,
    icon: AutoAwesome,
    badge: assistantCount > 0 ? <Badge badgeContent={<Tooltip title={'Clear assistant'}>
      <Span onClick={(e) => {
              clearAssistantEvent?.();
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e) => e.stopPropagation()}>
        {assistantCount}
      </Span>
    </Tooltip>} clickable={true} color="badgeWhite"/> : null,
    ...assistantAction,
    ActionButtonProps: {
      color: 'default',
      variant: 'outlined',
      plainBorder: true,
      radius: 'round',
      ...assistantAction?.ActionButtonProps
    },
    ActionIconButtonProps: {
      ...assistantAction?.ActionIconButtonProps
    }
  }), [assistantAction, assistantCount, mdDown, clearAssistantEvent, toggleAssistantEvent]);

  const toggleViewsEvent = useEffectEvent(tableProvider.toggleViews);
  const viewActionMemo = useMemo(() => ({
    onClick: () => {
      toggleViewsEvent?.();
    },
    label: !mdDown ? 'Views' : null,
    tooltip: mdDown ? 'Views' : null,
    icon: ViewHeadline,
    ...viewAction,
    ActionButtonProps: {
      color: 'default',
      variant: 'outlined',
      plainBorder: true,
      radius: 'round',
      ...viewAction?.ActionButtonProps
    },
    ActionIconButtonProps: {
      ...viewAction?.ActionIconButtonProps
    }
  }), [viewAction, mdDown, toggleViewsEvent]);

  const setFilterEvent = useEffectEvent(tableProvider.listState.setFilter);
  const toggleFiltersEvent = useEffectEvent(tableProvider.toggleFilters);
  const filterActionMemo = useMemo(() => ({
    onClick: () => {
      toggleFiltersEvent?.();
    },
    label: !mdDown ? 'Filters' : null,
    tooltip: mdDown ? 'Filters' : null,
    icon: FilterList,
    badge: filterCount > 0 ? <Badge badgeContent={<Tooltip title={'Clear filters'}>
      <Span onClick={(e) => {
              setFilterEvent?.([]);
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e) => e.stopPropagation()}>
        {filterCount}
      </Span>
    </Tooltip>} clickable={true} color="error"/> : null,
    ...filterAction,
    ActionButtonProps: {
      color: 'default',
      variant: 'outlined',
      plainBorder: true,
      radius: 'round',
      ...filterAction?.ActionButtonProps
    },
    ActionIconButtonProps: {
      ...filterAction?.ActionIconButtonProps
    }
  }), [filterAction, filterCount, setFilterEvent, mdDown, toggleFiltersEvent]);

  const resultsCount = utils.formatNumber(tableProvider.list?.meta?.resultsCount ?? 0);

  innerProps.title = innerProps.title ?? `${resultsCount} result${(tableProvider.list?.meta?.resultsCount ?? 0) === 1 ? '' : 's'}`;
  innerProps.rightFields = innerProps.rightFields ?? (
    hideSearch ? null : searchFields
  );
  innerProps.onRightChange = innerProps.onRightChange ?? (
    hideSearch ? null : handleSearch
  );
  innerProps.rightActions = innerProps.rightActions ?? [
    hideSave ? null : saveActionMemo,
    hideAssistant ? null : assistantActionMemo,
    hideView ? null : viewActionMemo,
    hideFilter ? null : filterActionMemo,
    hideColumn ? null : columnActionMemo,
    hideIndex ? null : indexActionMemo
  ].filter((_) => (_));

  innerProps.isLoading = innerProps.isLoading ?? (isLoading || !utils.isDefined(tableProvider.list?.meta?.resultsCount));

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledTableBar ref={ref} {...innerProps}/>
});

TableBar.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  searchField: PropTypes.object,
  indexAction: PropTypes.object,
  assistantAction: PropTypes.object,
  viewAction: PropTypes.object,
  filterAction: PropTypes.object,
  columnAction: PropTypes.object,
  saveAction: PropTypes.object,
  hideSearch: PropTypes.bool,
  hideIndex: PropTypes.bool,
  hideAssistant: PropTypes.bool,
  hideView: PropTypes.bool,
  hideFilter: PropTypes.bool,
  hideColumn: PropTypes.bool,
  hideSave: PropTypes.bool
};

export default TableBar;
